import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useUser } from "@clerk/react";
import { Switch } from "@/components/ui/switch";
import BioEditor, { BIO_MAX } from "@/components/BioEditor";

export default function ProfileEditor({ api, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me").then(r => setForm(f => f ?? r.data)).catch(() => toast.error("Could not load your profile"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) return toast.error("Artist name is required");
    if ((form.bio || "").length > BIO_MAX) return toast.error(`Bio must be ${BIO_MAX} characters or fewer`);
    setSaving(true);
    try {
      const r = await api.put("/me", {
        display_name: form.display_name, bio: form.bio, genre: form.genre, is_artist: true,
        show_account_photo: !!form.show_account_photo,
      });
      setForm(r.data);
      toast.success(`Saved. Your tracks now show "${r.data.display_name}"`);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full bg-transparent border-b border-neutral-700 focus:border-red-600 outline-none py-2 mt-2 transition-colors";
  const label = "font-mono-ui text-[11px] uppercase tracking-[0.25em] text-neutral-400";

  return (
    <form onSubmit={save} className="hard-card mt-12 p-8" data-testid="profile-editor">
      <div className="font-mono-ui text-[11px] uppercase tracking-[0.3em] text-red-500">Public Profile</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <label className={`${label} block mt-6`}>Artist Name</label>
          <input data-testid="profile-name-input" value={form.display_name} onChange={set("display_name")} maxLength={80}
                 className={`${field} font-display text-2xl font-bold`} />
          <label className={`${label} block mt-6`}>Genre</label>
          <input data-testid="profile-genre-input" value={form.genre} onChange={set("genre")} className={`${field} font-mono-ui text-sm`} />
        </div>
        <AccountPhotoToggle checked={!!form.show_account_photo}
                            onChange={(v) => setForm({ ...form, show_account_photo: v })} />
      </div>
      <div>
        <label className={`${label} block mt-8`}>Bio</label>
        <BioEditor value={form.bio || ""} onChange={(bio) => setForm({ ...form, bio })} />
        <button type="submit" disabled={saving} data-testid="profile-save-btn"
                className="mt-6 self-start bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.3em] transition-colors">
          {saving ? "Saving..." : "Save Profile"}
        </button>
        <div className="font-mono-ui text-[11px] text-neutral-500 mt-3">Your new name also updates all of your released tracks.</div>
      </div>
    </form>
  );
}

function AccountPhotoToggle({ checked, onChange }) {
  const { user } = useUser();
  const hasImage = !!user?.hasImage;
  return (
    <div className="mt-6 border border-neutral-900 p-5 flex gap-5 items-start self-start" data-testid="account-photo-section">
      {hasImage
        ? <img src={user.imageUrl} alt="Your account picture" className="w-16 h-16 rounded-full object-cover border border-neutral-800 shrink-0" data-testid="account-photo-preview" />
        : <div className="w-16 h-16 rounded-full border border-dashed border-neutral-700 shrink-0" />}
      <div className="flex-1">
        <div className="flex items-center justify-between gap-4">
          <label htmlFor="account-photo-switch" className="font-display font-bold leading-tight">
            Show my account profile picture on my public artist page
          </label>
          <Switch id="account-photo-switch" checked={checked} onCheckedChange={onChange} disabled={!hasImage && !checked}
                  data-testid="account-photo-switch" />
        </div>
        <div className="font-mono-ui text-[11px] text-neutral-500 mt-2 leading-relaxed">
          {hasImage
            ? "Off by default. Only the picture is shared, never your account name. Changes to your account picture appear automatically. Song and album covers are not affected. Click Save Profile to apply."
            : "Add a picture to your account (click your profile picture, top right, then Manage account) to use this option."}
        </div>
      </div>
    </div>
  );
}
